/**
 * Firebase Realtime Database Service
 *
 * Initializes Firebase and provides real-time listeners for:
 * - Queue updates (patient positions, wait times)
 * - Appointment status changes
 * - Notification badges (unread counts)
 * - Doctor availability status
 *
 * Falls back gracefully when Firebase is not configured.
 */
import { initializeApp } from 'firebase/app';
import {
  getDatabase,
  ref,
  onValue,
  off,
  set,
  update,
  push,
  remove,
  get,
  onChildAdded,
  onChildChanged,
  onChildRemoved,
  serverTimestamp,
} from 'firebase/database';

let app = null;
let database = null;
let initialized = false;

/**
 * Initialize Firebase with config from the API or environment
 * @param {Object} config - Firebase web config object
 * @returns {boolean} Whether initialization succeeded
 */
export const initFirebase = (config) => {
  if (initialized) return true;

  if (!config || !config.apiKey || !config.databaseURL) {
    console.warn('Firebase not configured — running in offline mode');
    return false;
  }

  try {
    app = initializeApp(config);
    database = getDatabase(app);
    initialized = true;
    return true;
  } catch (error) {
    console.error('Firebase initialization failed:', error);
    return false;
  }
};

/**
 * Check if Firebase is initialized and available
 */
export const isFirebaseReady = () => initialized && database !== null;

/**
 * Get the base path for a hospital tenant
 */
const getBasePath = (adminId) => `hospitals/${adminId}`;

// ── Queue Listeners ──────────────────────────────────────

/**
 * Subscribe to live queue updates for a specific date
 * @param {number} adminId
 * @param {string} date - YYYY-MM-DD
 * @param {function} callback - Called with queue data array
 * @returns {function} Unsubscribe function
 */
export const subscribeToQueue = (adminId, date, callback) => {
  if (!isFirebaseReady()) return () => {};

  const queueRef = ref(database, `${getBasePath(adminId)}/queue/${date}`);
  const unsubscribe = onValue(queueRef, (snapshot) => {
    const data = snapshot.val();
    if (!data) {
      callback([]);
      return;
    }
    // Convert object to sorted array
    const queue = Object.entries(data)
      .map(([id, item]) => ({ id, ...item }))
      .sort((a, b) => (a.position || 0) - (b.position || 0));
    callback(queue);
  });

  return () => off(queueRef, 'value', unsubscribe);
};

// ── Appointment Status Listeners ─────────────────────────

/**
 * Subscribe to a single appointment's status updates
 * @param {number} adminId
 * @param {number} appointmentId
 * @param {function} callback - Called with status data
 * @returns {function} Unsubscribe function
 */
export const subscribeToAppointment = (adminId, appointmentId, callback) => {
  if (!isFirebaseReady()) return () => {};

  const aptRef = ref(database, `${getBasePath(adminId)}/appointments/${appointmentId}`);
  const unsubscribe = onValue(aptRef, (snapshot) => {
    callback(snapshot.val());
  });

  return () => off(aptRef, 'value', unsubscribe);
};

// ── Doctor Availability Listeners ────────────────────────

/**
 * Subscribe to all doctors' availability statuses
 * @param {number} adminId
 * @param {function} callback - Called with {doctorId: statusData} object
 * @returns {function} Unsubscribe function
 */
export const subscribeToDoctorStatuses = (adminId, callback) => {
  if (!isFirebaseReady()) return () => {};

  const doctorsRef = ref(database, `${getBasePath(adminId)}/doctors`);
  const unsubscribe = onValue(doctorsRef, (snapshot) => {
    callback(snapshot.val() || {});
  });

  return () => off(doctorsRef, 'value', unsubscribe);
};

/**
 * Subscribe to a single doctor's status
 * @param {number} adminId
 * @param {number} doctorId
 * @param {function} callback
 * @returns {function} Unsubscribe function
 */
export const subscribeToDoctorStatus = (adminId, doctorId, callback) => {
  if (!isFirebaseReady()) return () => {};

  const docRef = ref(database, `${getBasePath(adminId)}/doctors/${doctorId}`);
  const unsubscribe = onValue(docRef, (snapshot) => {
    callback(snapshot.val());
  });

  return () => off(docRef, 'value', unsubscribe);
};

// ── Notification Listeners ───────────────────────────────

/**
 * Subscribe to unread notification count
 * @param {number} adminId
 * @param {number} userId
 * @param {function} callback - Called with count number
 * @returns {function} Unsubscribe function
 */
export const subscribeToUnreadCount = (adminId, userId, callback) => {
  if (!isFirebaseReady()) return () => {};

  const countRef = ref(database, `${getBasePath(adminId)}/notifications/${userId}/unread_count`);
  const unsubscribe = onValue(countRef, (snapshot) => {
    callback(snapshot.val() || 0);
  });

  return () => off(countRef, 'value', unsubscribe);
};

/**
 * Subscribe to new notifications (child_added)
 * @param {number} adminId
 * @param {number} userId
 * @param {function} callback - Called for each new notification
 * @returns {function} Unsubscribe function
 */
export const subscribeToNewNotifications = (adminId, userId, callback) => {
  if (!isFirebaseReady()) return () => {};

  const itemsRef = ref(database, `${getBasePath(adminId)}/notifications/${userId}/items`);

  const addedHandler = onChildAdded(itemsRef, (snapshot) => {
    callback({
      key: snapshot.key,
      ...snapshot.val(),
    });
  });

  return () => off(itemsRef, 'child_added', addedHandler);
};

/**
 * Subscribe to notification updates (read status changes)
 * @param {number} adminId
 * @param {number} userId
 * @param {function} callback
 * @returns {function} Unsubscribe function
 */
export const subscribeToNotificationChanges = (adminId, userId, callback) => {
  if (!isFirebaseReady()) return () => {};

  const itemsRef = ref(database, `${getBasePath(adminId)}/notifications/${userId}/items`);

  const changedHandler = onChildChanged(itemsRef, (snapshot) => {
    callback({
      key: snapshot.key,
      ...snapshot.val(),
    });
  });

  return () => off(itemsRef, 'child_changed', changedHandler);
};

// ── Dashboard Stats Listener ─────────────────────────────

/**
 * Subscribe to live dashboard statistics
 * @param {number} adminId
 * @param {function} callback
 * @returns {function} Unsubscribe function
 */
export const subscribeToDashboardStats = (adminId, callback) => {
  if (!isFirebaseReady()) return () => {};

  const statsRef = ref(database, `${getBasePath(adminId)}/dashboard`);
  const unsubscribe = onValue(statsRef, (snapshot) => {
    callback(snapshot.val());
  });

  return () => off(statsRef, 'value', unsubscribe);
};

// ── Cleanup ──────────────────────────────────────────────

/**
 * Disconnect all Firebase listeners (call on logout)
 */
export const disconnectFirebase = () => {
  // Firebase SDK handles connection lifecycle automatically
  // Individual listeners are cleaned up via their unsubscribe functions
  initialized = false;
  database = null;
  app = null;
};

export default {
  initFirebase,
  isFirebaseReady,
  subscribeToQueue,
  subscribeToAppointment,
  subscribeToDoctorStatuses,
  subscribeToDoctorStatus,
  subscribeToUnreadCount,
  subscribeToNewNotifications,
  subscribeToNotificationChanges,
  subscribeToDashboardStats,
  disconnectFirebase,
};
